import { calcWidgetCurve, prepareRecentAnalyticsDateRange } from 'util/appUtils';

const FIELDS = ['records', 'users', 'queries', 'curve'].join('+');

const REPORTS = Object.freeze({
  top_queries: {
    path: '/system/analytics/search/popular',
    query: { type: 'search' },
  },
  zero_results: {
    path: '/system/analytics/search/queriesWithoutResults',
    query: { type: 'all' },
  },
  lost_opportunities: {
    path: '/system/analytics/search/lostOpportunities',
    query: {},
  },
});

const optionalRequest = promise => promise.then(
  data => ({ data, unavailable: false }),
  () => ({ data: null, unavailable: true }),
);

function normalizeRecord(raw) {
  return {
    query: raw.query,
    searches: raw.searches ?? raw.users ?? raw.total ?? 0,
    click: raw.click ?? 0,
    noaction: raw.noaction ?? 0,
    ctr: raw.ctr ?? 0,
  };
}

function normalizeCurve(curve, reportType) {
  if (!Array.isArray(curve) || curve.length === 0) return { data: [] };
  const calculated = calcWidgetCurve(curve);
  return {
    data: Object.keys(calculated).map((key) => {
      const row = calculated[key];
      if (reportType === 'top_queries') {
        return {
          total: row.total ?? 0,
          click: row.click ?? 0,
          ctr: Number(((row.ctr ?? 0) * 100).toFixed(2)),
          label: row.label,
        };
      }
      if (reportType === 'zero_results') {
        return { pv: row.total ?? 0, label: row.label };
      }
      return { total: row.total ?? 0, label: row.label };
    }),
  };
}

/** Cloud adapter for analytics reports shared with the plugin admin UI. */
export function createLegacyAnalyticsApi({ client } = {}) {
  let resolvedClient = client || null;
  const getClient = () => {
    if (!resolvedClient) {
      // eslint-disable-next-line global-require
      resolvedClient = require('util/appUtils').wsClient;
    }
    return resolvedClient;
  };

  return {
    async loadQueryDetail(shopId, query, {
      from = null,
      to = null,
      timeframeDays = null,
    } = {}) {
      const normalizedQuery = String(query || '').trim();
      if (!normalizedQuery) throw new Error('query is required');
      const range = (!from && !to && timeframeDays)
        ? prepareRecentAnalyticsDateRange(timeframeDays)
        : { from, to };
      const commonQuery = {
        token: shopId,
        from: range.from,
        to: range.to,
        query: normalizedQuery,
      };
      const [data, relatedResult, relatedProductsResult] = await Promise.all([
        getClient().prepareRequest('GET', 'system/analytics/search/curve', {
          qs: {
            ...commonQuery,
            fields: FIELDS,
            type: 'all',
            appId: 'search',
          },
        }),
        optionalRequest(getClient().prepareRequest('GET', '/system/analytics/search/related', {
          qs: commonQuery,
        })),
        optionalRequest(getClient().prepareRequest('GET', '/system/analytics/search/related/products', {
          qs: commonQuery,
        })),
      ]);
      const related = relatedResult.data;
      const relatedProducts = relatedProductsResult.data;
      const curve = (data?.curve || []).map((row) => {
        const date = row._id
          ? `${row._id.year}-${String(row._id.month).padStart(2, '0')}-${String(row._id.day).padStart(2, '0')}`
          : row.date;
        const searches = Number(row.total || 0);
        const clicks = Number(row.click || 0);
        return {
          date,
          searches,
          clicks,
          noActions: Number(row.noaction ?? Math.max(0, searches - clicks)),
          ctr: Number(row.ctr ?? (searches > 0 ? clicks / searches : 0)),
        };
      });
      const metrics = curve.reduce((total, row) => ({
        searches: total.searches + row.searches,
        clicks: total.clicks + row.clicks,
        noActions: total.noActions + row.noActions,
        ctr: 0,
      }), {
        searches: 0, clicks: 0, noActions: 0, ctr: 0,
      });
      metrics.ctr = metrics.searches > 0 ? metrics.clicks / metrics.searches : 0;
      return {
        query: normalizedQuery,
        metrics,
        curve,
        relatedQueries: (related?.records || []).map(row => ({
          query: row.query || row._id?.query,
          count: Number(row.count || 0),
        })).filter(row => row.query),
        relatedProducts: (relatedProducts?.records || []).map(row => ({
          productId: String(row.productId),
          name: row.name || null,
          count: Number(row.count || 0),
        })).filter(row => row.productId !== 'undefined' && row.productId !== 'null'),
        relatedQueriesUnavailable: relatedResult.unavailable,
        relatedProductsUnavailable: relatedProductsResult.unavailable,
        relatedQueriesSampled: related?.sampled === true,
        relatedQueriesSampleSize: Number(related?.sessionSampleSize || 0),
      };
    },

    async loadReport(shopId, reportType, {
      limit = 50,
      from = null,
      to = null,
    } = {}) {
      const report = REPORTS[reportType];
      if (!report) {
        throw new Error(`Analytics report "${reportType}" is not available in cloud.`);
      }
      const data = await getClient().prepareRequest('GET', report.path, {
        qs: {
          token: shopId,
          from,
          to,
          fields: FIELDS,
          limit,
          ...report.query,
        },
      });
      return {
        data: (data?.records || []).map(normalizeRecord),
        users: data?.users || { total: 0, filteredTotal: 0, otherTotal: 0 },
        queries: data?.queries || {
          total: 0, filteredTotal: 0, otherTotal: 0, ctr: 0,
        },
        curve: normalizeCurve(data?.curve || [], reportType),
      };
    },
  };
}
